import type { IntervalConfig, Report, ReportMetadata } from '../types';
import { calculateNextDueDate } from './dateService';
import { speichereFrischGeladen, type FrischSpeichernDeps } from './frischSpeichern';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FOLGETERMIN AUS DER LISTE SETZEN (Verschieben / Überspringen)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis zum 21.10.2026 speicherten Dashboard, Projektliste und Kanban den
 * Listeneintrag selbst als Projekt — und der trägt nur `metadata`. Jeder
 * Klick auf „+7 Tage" oder „Überspringen" löschte Unterlage,
 * Vorankündigung, Einstellungen und alles andere außerhalb der Metadaten
 * (listenAuszug.ts). Jetzt:
 *
 *   1. das Projekt VOLL laden (`loadProjectById`, frischSpeichern.ts),
 *   2. das Ziel AUS DIESEM STAND berechnen und NUR den Folgetermin ändern
 *      (`metadata.intervalConfig.nextScheduledDate`; die Spalte
 *      `next_scheduled_date` leitet `saveProjectToCloud` daraus ab),
 *   3. mit dem Anker dieses Ladens speichern, `onConflict: 'throw'` — bei
 *      einem Konflikt frisch laden und das Ziel NEU berechnen. Kein
 *      Direkt-Update mit `updated_at` (das bricht den Anker, Vorfall 26073001).
 *
 * ─── Reihenfolge ───────────────────────────────────────────────────────────
 * Die Aufträge EINES Projekts laufen nacheinander (Kette je Projekt), und
 * jeder rechnet sein Ziel aus dem Stand, den der vorige hinterlassen hat. Zwei
 * schnelle „+7 Tage" ergeben so +14 — nie wieder +7, weil der ältere Klick
 * nach einem Konflikt sein altes, absolutes Ziel über den neueren geschrieben
 * hätte (Codex-Befund 21.10.2026). Ein gescheiterter Auftrag hält die Kette
 * nicht auf.
 *
 * Was die Liste vorab anzeigt, rechnet der Aufrufer mit denselben Funktionen
 * aus dem Listeneintrag (`zielNachTagen`, `zielUebersprungen`); gilt am Ende
 * ein anderer Wert, meldet ihn das Ergebnis.
 */

/** Was das Ziel kennt: der frisch geladene Folgetermin und die Metadaten. */
export interface FolgeterminStand {
    /** Spalte `next_scheduled_date` (jüngere Quelle), sonst null. */
    naechster: string | null;
    metadata: ReportMetadata;
}

/** Berechnet den neuen Folgetermin; '' oder null heißt „nichts zu tun". */
export type FolgeterminZiel = (stand: FolgeterminStand) => string | null;

/**
 * Der Stand nach dem Auftrag — geschrieben ODER, wenn es nichts zu tun gab,
 * der eben geladene. Die Liste gleicht sich in beiden Fällen daran ab: sie
 * kann einen Turnus zeigen, den jemand inzwischen entfernt hat, und soll ihn
 * danach nicht weiter zeigen (Codex-Befund 21.10.2026, Runde 2).
 */
export interface FolgeterminErgebnis {
    /** false: nichts geschrieben — kein Turnus, manueller Turnus, kein gültiges Ziel. */
    geaendert: boolean;
    nextScheduledDate: string | null;
    intervalConfig: IntervalConfig | undefined;
}

const heute = (): string => new Date().toISOString().split('T')[0];

/**
 * Der Termin, von dem aus verschoben wird — dieselbe Reihenfolge wie bisher
 * in App.handlePostpone/handleSkip: Spalte, dann JSON-Zweig, beim
 * Überspringen dann das Berichtsdatum, sonst heute.
 */
export const folgeterminBasis = (
    naechster: string | null | undefined,
    metadata: Pick<ReportMetadata, 'intervalConfig' | 'date'> | undefined,
    mitBerichtsdatum = false,
): string =>
    naechster
    || metadata?.intervalConfig?.nextScheduledDate
    || (mitBerichtsdatum ? metadata?.date : undefined)
    || heute();

/** „+N Tage" — wörtlich die bisherige Rechnung aus App.handlePostpone. */
export const zielNachTagen = (basis: string, tage: number): string => {
    const d = new Date(basis);
    d.setDate(d.getDate() + tage);
    return d.toISOString().split('T')[0];
};

/** „Überspringen": der nächste Turnustermin; '' ohne Turnus (nichts zu tun). */
export const zielUebersprungen = (basis: string, intervall: IntervalConfig | undefined): string =>
    intervall ? calculateNextDueDate(basis, intervall) : '';

/**
 * Der Bericht mit neuem Folgetermin — sonst unverändert. Dieselbe Form wie
 * bisher (`{ ...intervalConfig, nextScheduledDate }`).
 */
export const mitFolgetermin = (report: Report, nextScheduledDate: string): Report => ({
    ...report,
    metadata: {
        ...report.metadata,
        intervalConfig: { ...report.metadata.intervalConfig, nextScheduledDate } as IntervalConfig,
    },
});

// ─── Die Kette je Projekt ───────────────────────────────────────────────────
const ketten = new Map<string, Promise<unknown>>();

/** Hängt `job` an die Kette des Projekts — er startet erst, wenn der vorige fertig ist (gleich wie). */
export const nacheinander = <T>(projectId: string, job: () => Promise<T>): Promise<T> => {
    const vorher = ketten.get(projectId) ?? Promise.resolve();
    const lauf = vorher.then(() => job(), () => job());
    ketten.set(projectId, lauf);
    const aufraeumen = () => { if (ketten.get(projectId) === lauf) ketten.delete(projectId); };
    lauf.then(aufraeumen, aufraeumen);
    return lauf;
};

/**
 * Setzt den Folgetermin: in der Kette des Projekts, frisch geladen, Ziel aus
 * dem geladenen Stand. Liefert IMMER den aktuellen Stand (`geaendert` sagt,
 * ob geschrieben wurde). Wirft, wenn das Projekt nicht ladbar ist oder das
 * Speichern scheitert — dann ist nichts geschrieben worden.
 */
export const setzeFolgetermin = (
    projectId: string,
    ziel: FolgeterminZiel,
    deps?: FrischSpeichernDeps,
): Promise<FolgeterminErgebnis> =>
    nacheinander(projectId, async () => {
        const stand = await speichereFrischGeladen(projectId, ({ report, naechsterTermin }) => {
            const datum = ziel({ naechster: naechsterTermin, metadata: report.metadata });
            return datum ? mitFolgetermin(report, datum) : null;
        }, deps);
        const intervall = stand.report.metadata.intervalConfig;
        return {
            geaendert: stand.geschrieben,
            // Die Spalte ist die jüngere Quelle (wie beim Laden); geschrieben
            // stehen Spalte und JSON-Zweig ohnehin gleich.
            nextScheduledDate: stand.naechsterTermin ?? intervall?.nextScheduledDate ?? null,
            intervalConfig: intervall,
        };
    });

/** „+N Tage" ab dem aktuellen Folgetermin des Projekts. */
export const verschiebeUmTage = (projectId: string, tage: number, deps?: FrischSpeichernDeps) =>
    setzeFolgetermin(projectId, s => zielNachTagen(folgeterminBasis(s.naechster, s.metadata), tage), deps);

/** „Überspringen": der nächste Turnustermin ab dem aktuellen Folgetermin. */
export const ueberspringeTurnus = (projectId: string, deps?: FrischSpeichernDeps) =>
    setzeFolgetermin(projectId, s => zielUebersprungen(folgeterminBasis(s.naechster, s.metadata, true), s.metadata.intervalConfig), deps);

/** Ein fester Termin (Tests, künftige Aufrufer). */
export const speichereFolgetermin = (projectId: string, nextScheduledDate: string, deps?: FrischSpeichernDeps) =>
    setzeFolgetermin(projectId, () => nextScheduledDate, deps);

// ═══════════════════════════════════════════════════════════════════════════
// DIE ANZEIGE IN DER LISTE: vorab umstellen, am Ende richtigstellen
// ═══════════════════════════════════════════════════════════════════════════
//
// Die Liste zeigt den neuen Termin sofort (optimistisch). Scheitert der
// Auftrag — etwa ohne Netz, wo auch das Neuladen der Liste nichts ausrichtet
// —, muss die Anzeige auf den letzten BESTÄTIGTEN Stand zurück; sonst wäre
// der nie gespeicherte Termin die Basis des nächsten Klicks. Zurückgestellt
// (oder auf den tatsächlich gespeicherten Wert gesetzt) wird aber nur, wenn
// seitdem keine neuere Aktion an diesem Projekt angestoßen wurde: deren
// Vorschau steht dann in der Liste, und ihr eigener Abschluss entscheidet.

/** Der Termin, wie ihn ein Listeneintrag zeigt: Spalte + JSON-Zweig. */
export interface ListenTermin {
    datum: string | null;
    intervall: IntervalConfig | undefined;
}

interface Verlauf {
    nr: number;
    laufend: number;
    bestaetigt: ListenTermin;
}

export interface FolgeterminAnzeige {
    /** Vor dem Vorab-Umstellen: `angezeigt` ist, was die Liste JETZT zeigt. Liefert die Nummer der Aktion. */
    beginne: (projectId: string, angezeigt: ListenTermin) => number;
    /**
     * Abgeschlossen. `aktuell` ist der Stand vom Server — geschrieben oder, bei
     * „nichts zu tun", frisch geladen (`null` = unbekannt, der bestätigte
     * bleibt). Liefert, was die Liste zeigen soll — oder null: nicht anfassen.
     */
    erfolg: (projectId: string, nr: number, aktuell: ListenTermin | null) => ListenTermin | null;
    /** Gescheitert. Liefert den letzten bestätigten Stand — oder null, wenn eine neuere Aktion läuft. */
    fehler: (projectId: string, nr: number) => ListenTermin | null;
}

export const erstelleFolgeterminAnzeige = (): FolgeterminAnzeige => {
    const je = new Map<string, Verlauf>();
    const abschluss = (projectId: string, nr: number, gespeichert: ListenTermin | null): ListenTermin | null => {
        const v = je.get(projectId);
        if (!v) return null;
        v.laufend -= 1;
        if (gespeichert) v.bestaetigt = gespeichert;
        const anzeigen = v.nr === nr ? v.bestaetigt : null;
        if (v.laufend <= 0) je.delete(projectId);
        return anzeigen;
    };
    return {
        beginne: (projectId, angezeigt) => {
            const v = je.get(projectId);
            if (v && v.laufend > 0) {
                // Es läuft schon etwas: der bestätigte Stand ist der von
                // davor, NICHT die Vorschau, die die Liste gerade zeigt.
                v.nr += 1;
                v.laufend += 1;
                return v.nr;
            }
            const neu: Verlauf = { nr: (v?.nr ?? 0) + 1, laufend: 1, bestaetigt: angezeigt };
            je.set(projectId, neu);
            return neu.nr;
        },
        erfolg: (projectId, nr, aktuell) => abschluss(projectId, nr, aktuell),
        fehler: (projectId, nr) => abschluss(projectId, nr, null),
    };
};

/** Ein Listeneintrag mit gesetztem Termin — Spalte und JSON-Zweig; alles andere (auch die Auszug-Kennung) bleibt. */
export const mitListenTermin = <T extends { next_scheduled_date?: string | null; data?: { metadata?: object } }>(
    eintrag: T,
    termin: ListenTermin,
): T => ({
    ...eintrag,
    next_scheduled_date: termin.datum,
    data: {
        ...eintrag.data,
        metadata: { ...eintrag.data?.metadata, intervalConfig: termin.intervall },
    },
});
